import type { getRxStorageDexie } from 'rxdb/plugins/storage-dexie';
import type { RxPlugin } from 'rxdb';
import { defineStoredJson } from '../utils/storage';

type DexieSettings = NonNullable<Parameters<typeof getRxStorageDexie>[0]>;

/**
 * Where the database open spends its time, collection by collection.
 *
 * The boot trace only says that `addCollections` has not come back. Behind that
 * one call lie a separate IndexedDB per collection (opened by Dexie) and RxDB's
 * own work afterwards (its bookkeeping store, the migration check) — and a
 * report that cannot tell them apart cannot say whether the device is slow,
 * one database is held, or a migration is running.
 *
 * Kept in sessionStorage like the boot trace, written synchronously at every
 * step, and the previous open is kept too: after a hang the interesting run is
 * the one before the reload.
 */

/** Milliseconds since the document started loading, per step. */
export interface CollectionTiming {
    /** Dexie began opening this collection's IndexedDB. */
    opening?: number;
    /** IndexedDB handed the database over. */
    open?: number;
    /** The open waits on another connection that will not let go. */
    blocked?: number;
    /** RxDB finished preparing the collection (before its migration check). */
    ready?: number;
}

export type OpenProfile = Record<string, CollectionTiming>;

function defineProfile(key: string) {
    return defineStoredJson<OpenProfile>({
        key,
        area: 'session',
        fallback: () => ({}),
        decode: (parsed) =>
            parsed !== null &&
            typeof parsed === 'object' &&
            !Array.isArray(parsed)
                ? (parsed as OpenProfile)
                : undefined
    });
}

const currentProfile = defineProfile('db_open_profile');
const previousProfile = defineProfile('db_open_profile_previous');

/** This document's open, step by step. */
export function readOpenProfile(): OpenProfile {
    return currentProfile.read();
}

/** The open of the document before — the interesting one after a reload. */
export function readPreviousOpenProfile(): OpenProfile {
    return previousProfile.read();
}

/**
 * Only while the open runs: the same storage later opens the replications'
 * checkpoint stores, which would otherwise read as collections stuck in RxDB.
 */
let recording = false;

/** Start a fresh record, moving the last one aside. Once per open. */
export function beginOpenProfile(): void {
    const last = currentProfile.read();
    if (Object.keys(last).length > 0) {
        previousProfile.write(last);
    }
    currentProfile.clear();
    recording = true;
}

/** The open is over, whichever way it ended. */
export function endOpenProfile(): void {
    recording = false;
}

function markCollection(collection: string, step: keyof CollectionTiming) {
    if (!recording || collection.startsWith('rx-')) {
        return;
    }
    const profile = currentProfile.read();
    const timing = profile[collection] ?? {};
    // The first time counts: a second open of the same name (a migration's old
    // copy) must not hide how long the first one took.
    if (timing[step] === undefined) {
        timing[step] = performance.now();
    }
    currentProfile.write({ ...profile, [collection]: timing });
}

/** `rxdb-dexie-ast_data_app--3--findings` → `findings`. */
function collectionOfDexieName(dexieName: string): string {
    const parts = dexieName.split('--');
    return parts[parts.length - 1] ?? dexieName;
}

/**
 * Settings for `getRxStorageDexie`: RxDB calls `onCreate` right before it opens
 * a collection's IndexedDB, which is the only place to watch that open.
 */
export const profiledDexieSettings: DexieSettings = {
    onCreate(dexie, dexieName) {
        const collection = collectionOfDexieName(dexieName);
        markCollection(collection, 'opening');
        dexie.on('ready', () => {
            markCollection(collection, 'open');
        });
        dexie.on('blocked', () => {
            markCollection(collection, 'blocked');
        });
    }
};

/**
 * `indexedDB.databases()` during the open — shared, and counted.
 *
 * Dexie calls it before EVERY open on what it takes for Safari (Chrome on iOS
 * included: `CriOS … Safari/`, no `Chrome/`) and repeats the call every 100 ms
 * until the first one answers. It only waits for an answer, any answer — a
 * workaround for a Safari 14 bug. With one database per collection that is two
 * dozen such loops at once, and WebKit answers each call by opening every
 * database file of the origin, on the one queue that also serves every other
 * IndexedDB open. So while our open runs, all callers share ONE call in flight:
 * same answer, a fraction of the work.
 *
 * The counts go into the fault report: `requested` against `sent` says how
 * much the sharing saved, `longest` how slow the browser was to answer.
 */
export interface DatabaseListing {
    /** How often somebody asked. */
    requested: number;
    /** How often the browser was actually asked. */
    sent: number;
    answered: number;
    /** The slowest answer, in milliseconds. */
    longest: number;
    /** How many databases the origin holds, from the first answer. */
    found?: number;
}

function defineListing(key: string) {
    return defineStoredJson<DatabaseListing | null>({
        key,
        area: 'session',
        fallback: () => null,
        decode: (parsed) =>
            parsed !== null &&
            typeof parsed === 'object' &&
            'requested' in parsed
                ? (parsed as DatabaseListing)
                : undefined
    });
}

const currentListing = defineListing('db_open_listing');
const previousListing = defineListing('db_open_listing_previous');

export function readDatabaseListing(): DatabaseListing | null {
    return currentListing.read();
}

export function readPreviousDatabaseListing(): DatabaseListing | null {
    return previousListing.read();
}

/**
 * How long a call may stay unanswered before the browser is asked again. Dexie
 * asks every 100 ms per open; once a second for all of them together still
 * frees a Safari 14 that needs the nudge, at a fraction of the load.
 */
const RESEND_AFTER_MS = 1000;

/**
 * Share `indexedDB.databases()` among its callers until the returned undo is
 * called. A call arriving while one is in flight gets the first answer of that
 * round; an unanswered round is asked again once a second; a call after the
 * answer starts a new round.
 */
export function shareDatabaseListing(): () => void {
    const last = currentListing.read();
    if (last) {
        previousListing.write(last);
    }
    currentListing.clear();
    if (typeof indexedDB === 'undefined' || !indexedDB.databases) {
        return () => {};
    }
    const factory = indexedDB;
    const original = factory.databases;
    const listing: DatabaseListing = {
        requested: 0,
        sent: 0,
        answered: 0,
        longest: 0
    };
    const store = () => currentListing.write({ ...listing });
    /** The answer everybody waiting right now shares, and when we last asked. */
    let shared: Promise<IDBDatabaseInfo[]> | null = null;
    let settleShared: (answer: Promise<IDBDatabaseInfo[]>) => void = () => {};
    let lastSentAt = 0;
    const send = () => {
        const startedAt = performance.now();
        lastSentAt = startedAt;
        listing.sent += 1;
        // Written here and on answers, not per request: Dexie asks a few
        // hundred times a second, on the thread this is meant to relieve.
        store();
        const answer = original.call(factory);
        void answer.then(
            (databases) => {
                listing.answered += 1;
                listing.longest = Math.max(
                    listing.longest,
                    performance.now() - startedAt
                );
                listing.found ??= databases.length;
                store();
            },
            () => {}
        );
        // Whichever call settles first settles the round — like Dexie's own
        // loop, which takes any answer — and the next request starts afresh.
        void answer.then(
            () => settleShared(answer),
            () => settleShared(answer)
        );
    };
    factory.databases = () => {
        listing.requested += 1;
        if (!shared) {
            shared = new Promise<IDBDatabaseInfo[]>((resolve) => {
                settleShared = (answer) => {
                    shared = null;
                    settleShared = () => {};
                    resolve(answer);
                };
            });
            send();
        } else if (performance.now() - lastSentAt >= RESEND_AFTER_MS) {
            // The call in flight may be one that never answers — the Safari 14
            // bug Dexie's loop exists for. Ask again, just not ten times a
            // second per collection.
            send();
        }
        return shared;
    };
    return () => {
        factory.databases = original;
        store();
    };
}

/** `indexedDB.databases(): 214 Anfragen, 3 an den Browser, 3 beantwortet, längste 4100 ms, 87 Datenbanken` */
export function formatDatabaseListing(
    listing: DatabaseListing | null
): string | null {
    if (!listing || listing.requested === 0) {
        return null;
    }
    return (
        `indexedDB.databases(): ${listing.requested} Anfragen, ${listing.sent} an den Browser, ` +
        `${listing.answered} beantwortet, längste ${Math.round(listing.longest)} ms` +
        (listing.found === undefined ? '' : `, ${listing.found} Datenbanken`)
    );
}

/** Notes when RxDB has a collection ready; the migration check follows. */
export const RxDBOpenProfilePlugin: RxPlugin = {
    name: 'ast-open-profile',
    rxdb: true,
    hooks: {
        createRxCollection: {
            after: ({ collection }: { collection: { name: string } }) => {
                markCollection(collection.name, 'ready');
            }
        }
    }
};

function duration(from: number | undefined, to: number | undefined) {
    return from === undefined || to === undefined ? null : to - from;
}

/** How many of the slowest IndexedDB opens a report names. */
const SLOWEST_SHOWN = 4;

/**
 * The profile as a report reads it — what is still pending first, because that
 * is the answer to "where does it hang", then the slowest opens:
 *
 *     IndexedDB: 23 von 23 offen, zuletzt bei 21400 ms; langsamste: findings 8400 ms, finds 7900 ms
 *     Wartet auf IndexedDB: photos (seit 600 ms)
 *     Wartet auf RxDB: findings, finds
 */
export function formatOpenProfile(
    profile: OpenProfile,
    /** This document's clock; leave out for a previous document's profile. */
    now?: number
): string[] {
    const entries = Object.entries(profile);
    if (entries.length === 0) {
        return [];
    }
    const lines: string[] = [];
    const opened = entries.filter(([, timing]) => timing.open !== undefined);
    const lastOpen = Math.max(
        ...opened.map(([, timing]) => timing.open ?? 0),
        0
    );
    const slowest = opened
        .map(([name, timing]) => ({
            name,
            took: duration(timing.opening, timing.open) ?? 0
        }))
        .sort((firstEntry, secondEntry) => secondEntry.took - firstEntry.took)
        .slice(0, SLOWEST_SHOWN)
        .map((entry) => `${entry.name} ${Math.round(entry.took)} ms`);
    lines.push(
        `IndexedDB: ${opened.length} von ${entries.length} offen` +
            (opened.length > 0
                ? `, zuletzt bei ${Math.round(lastOpen)} ms; langsamste: ${slowest.join(', ')}`
                : '')
    );

    const waitingOnStorage = entries
        .filter(([, timing]) => timing.open === undefined)
        .map(
            ([name, timing]) =>
                `${name} (` +
                [
                    timing.blocked !== undefined ? 'blockiert' : null,
                    `begonnen bei ${Math.round(timing.opening ?? 0)} ms`,
                    now === undefined
                        ? null
                        : `seit ${Math.round(now - (timing.opening ?? now))} ms`
                ]
                    .filter((part) => part !== null)
                    .join(', ') +
                ')'
        );
    if (waitingOnStorage.length > 0) {
        lines.push(`Wartet auf IndexedDB: ${waitingOnStorage.join(', ')}`);
    }

    // The internal store is never a collection, so it never gets `ready`.
    const waitingOnRxdb = entries
        .filter(
            ([name, timing]) =>
                !name.startsWith('_rxdb') &&
                timing.open !== undefined &&
                timing.ready === undefined
        )
        .map(([name]) => name);
    if (waitingOnRxdb.length > 0) {
        lines.push(`Wartet auf RxDB: ${waitingOnRxdb.join(', ')}`);
    }
    return lines;
}
